import api from "./api";
import { clientDateHeaders, todayIso } from "./trainingStatus";
import { clock12 } from "./comingUp";
import { getWithRetry } from "./fetchWithRetry";

/**
 * Issue #1316 (Pick XI coach 1 of 3; epic #1072, D297/D302/D309) — the
 * frontend's calls to `/api/coach-games` (#1313). Every call sends the
 * caller's own day (`X-Client-Date`), because the API windows a game's date
 * and lists "today onwards" on the coach's day, not the server's.
 */

/** This coach's games from today on, soonest first. Rejects on a failed request. */
export async function listGames() {
  const res = await api.get("/coach-games", { headers: clientDateHeaders() });
  return res.data?.games || [];
}

/** One game in the coach's view (squad, sheets, plans). Rejects on a failed request. */
export async function getGame(id) {
  const res = await api.get(`/coach-games/${encodeURIComponent(id)}`, { headers: clientDateHeaders() });
  return res.data;
}

/** Schedule a game. `body.squad` is the ticked player ids. */
export async function scheduleGame(body) {
  const res = await api.post("/coach-games", body, { headers: clientDateHeaders() });
  return res.data;
}

/** Replace a game's details and squad (the API's `PUT` takes the whole body). */
export async function updateGame(id, body) {
  const res = await api.put(`/coach-games/${encodeURIComponent(id)}`, body, { headers: clientDateHeaders() });
  return res.data;
}

/**
 * Save a game's draft team sheet (`PUT /:id/sheet`, #1314): the XI in order
 * with tags, the 12th man, the bowling order and the volunteers. Returns the
 * game in the coach's view. Rejects on a failed request, and on a 400 whose
 * `code` is `not_in_squad`, `tags`, `twelfth_in_xi` or `bowling`.
 */
export async function saveSheet(id, body) {
  const res = await api.put(`/coach-games/${encodeURIComponent(id)}/sheet`, body, { headers: clientDateHeaders() });
  return res.data;
}

/**
 * Save a game's draft plan (`PUT /:id/plan`, #1314): the toss call, the three
 * things to remember, the plan texts, Arrive by, the team notes and whether
 * players see the plan. Nothing is sent to anyone. Returns the game in the
 * coach's view.
 */
export async function savePlan(id, body) {
  const res = await api.put(`/coach-games/${encodeURIComponent(id)}/plan`, body, { headers: clientDateHeaders() });
  return res.data;
}

/**
 * Save the day's sheet (`PUT /:id/day`, #1314; #745): the batting order with
 * any swap (`replaces_player_id`, `swap_reason`), the 12th man and the bowling
 * order, as the coach changed them on the day. The published team is not
 * touched. Returns the game in the coach's view, with `sheets.day` set. Rejects
 * on a failed request, on a 409 `not_published`, and on a 400 whose `code` is
 * `swap`, `not_in_squad`, `twelfth_in_xi`, `xi` or `bowling`.
 */
export async function saveDay(id, body) {
  const res = await api.put(`/coach-games/${encodeURIComponent(id)}/day`, body, { headers: clientDateHeaders() });
  return res.data;
}

/**
 * Publish the team (`POST /:id/publish`, #1314): the draft sheet becomes the
 * published one, with this plan, and each player in the squad is told (#1315).
 * Rejects on a failed request, and on a 400 whose `code` is `empty_team`.
 */
export async function publish(id, body) {
  const res = await api.post(`/coach-games/${encodeURIComponent(id)}/publish`, body, { headers: clientDateHeaders() });
  return res.data;
}

/**
 * The board's three marks on a game row (`CoachScheduleGame.dc.html`,
 * `games-list`). An unknown status reads as the base state, "Team not picked".
 */
export function gameStatusLabel(status) {
  if (status === "published") return "Team published";
  if (status === "draft") return "Draft";
  return "Team not picked";
}

/**
 * D316 Q3 (#745): a PUBLISHED game opens On the day on and after its date, and
 * Pick the XI before it. An unpublished game never does (the API refuses `day`
 * writes on it with 409 `not_published`). `today` is the coach's own local
 * date, the same one `X-Client-Date` carries; both are `YYYY-MM-DD`, so the
 * comparison is of calendar days, never of instants.
 */
export function opensOnTheDay(game, today = todayIso()) {
  return game?.status === "published" && !!game.match_date && game.match_date <= today;
}

/** D309: Team selection opens on the next game. The list is soonest first. */
export function nextGameId(list) {
  return list?.[0]?.game_id ?? null;
}

/** `2026-10-17` as `Sat 17 Oct`: the board's [DATE]. Never "Today" or "Tomorrow", so the sentence reads the same on any day. */
export function gameDateLabel(iso) {
  if (!iso) return "";
  return new Date(`${iso}T00:00:00Z`)
    .toLocaleDateString("en-NZ", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" })
    .replace(",", "");
}

/** The games-list second line: "[DATE] · 2:00 pm · [GROUND NAME]". The time and the ground are left out when absent. */
export function gameWhenLine(game) {
  const parts = [gameDateLabel(game?.match_date)];
  const time = clock12(game?.match_time);
  if (time) parts.push(time);
  if (game?.ground) parts.push(game.ground);
  return parts.filter(Boolean).join(" · ");
}

/**
 * Issue #1319 (Pick XI player; D300, D302, D315) — the player's side of a
 * coach's game: `/api/coach-games/mine` (#1315). The API sends only what a
 * squad player may read (the published team by names and tags, the shared
 * plan, the notes); nothing here asks for more.
 */

/** The player's coach's games from a year back to a year ahead (Coming up keeps those from today). Rejects on a failed request. */
export async function listMyGames() {
  // An idempotent read: one blip must not read as "no games" (#271).
  const res = await getWithRetry("/coach-games/mine", { headers: clientDateHeaders() });
  return res.data?.games || [];
}

/** One game as its squad player reads it. Rejects on a failed request (a 404 included). */
export async function getMyGame(id) {
  const res = await api.get(`/coach-games/mine/${encodeURIComponent(id)}`, { headers: clientDateHeaders() });
  return res.data;
}

/**
 * Q6 / D317: the player's line when they bowl, built with the planner's
 * proposal. The board draws only the "not in the bowling order" line; Martin
 * may change these two (D259). They are one constant so a ruling is one edit.
 * `{n}` is the batting place, `{m}` the place in the bowling order.
 */
export const BOWLING_LINES = {
  none: "You bat at {n}. You're not in the bowling order.",
  opener: "You bat at {n}. You open the bowling.",
  order: "You bat at {n}. You bowl at {m} in the order.",
};

/** Bowling places 1 and 2 are the opening bowlers (the API's `opening_bowlers`). */
const OPENER_PLACES = [1, 2];

/**
 * The sentence about the player's own place, from the `team` the API sends
 * (`team.me`). Null when there is no team yet, or when the API did not say where
 * the player bats (nothing is guessed). The 12th man and the bench are the
 * board's `twelfth` and `bench` states.
 */
export function myLine(team) {
  const me = team?.me;
  if (!me) return null;
  if (me.place === "twelfth") return "You're 12th man for this game · fields only.";
  if (me.place === "bench") return "You're in the squad, not in the team for this game. The team is below.";
  if (me.place !== "xi" || me.bat_order == null) return null;
  const bowl = me.bowl_order;
  const template =
    bowl == null ? BOWLING_LINES.none : OPENER_PLACES.includes(bowl) ? BOWLING_LINES.opener : BOWLING_LINES.order;
  return template.replace("{n}", String(me.bat_order)).replace("{m}", String(bowl));
}

/** The plan's toss line value: the board's "If we win the toss: Bat". An unknown value reads as nothing. */
export function tossLabel(toss) {
  if (toss === "bat") return "Bat";
  if (toss === "bowl") return "Bowl";
  if (toss === "on_the_day") return "On the day";
  return "";
}
